import React, { useState } from 'react'
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Chip,
  Button,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Alert,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  SelectChangeEvent,
} from '@mui/material'
import {
  CheckCircle as ApproveIcon,
  Cancel as RejectIcon,
  Edit as ModifyIcon,
} from '@mui/icons-material'
import {
  useGetAllCigarSubmissionsQuery,
  useReviewCigarSubmissionMutation,
  CigarSubmission,
} from '@store/api/submissionApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const STATUS_COLORS: Record<string, 'default' | 'warning' | 'success' | 'error' | 'info'> = {
  pending: 'warning',
  approved: 'success',
  rejected: 'error',
  modification_requested: 'info',
}

type ReviewAction = 'approve' | 'reject' | 'request_modification'

interface ReviewDialogState {
  open: boolean
  submission: CigarSubmission | null
  action: ReviewAction | null
  adminNotes: string
}

const AdminSubmissions: React.FC = () => {
  const [statusFilter, setStatusFilter] = useState<string>('pending')
  const [page] = useState(1)
  const [dialog, setDialog] = useState<ReviewDialogState>({
    open: false,
    submission: null,
    action: null,
    adminNotes: '',
  })
  const [successMessage, setSuccessMessage] = useState<string | null>(null)
  const [errorMessage, setErrorMessage] = useState<string | null>(null)

  const { data, isLoading, refetch } = useGetAllCigarSubmissionsQuery({
    page,
    limit: 50,
    status: statusFilter || undefined,
  })

  const [reviewSubmission, { isLoading: isReviewing }] = useReviewCigarSubmissionMutation()

  const handleStatusFilterChange = (e: SelectChangeEvent) => {
    setStatusFilter(e.target.value)
  }

  const openReviewDialog = (submission: CigarSubmission, action: ReviewAction) => {
    setDialog({ open: true, submission, action, adminNotes: '' })
    setSuccessMessage(null)
    setErrorMessage(null)
  }

  const closeDialog = () => {
    setDialog({ open: false, submission: null, action: null, adminNotes: '' })
  }

  const handleReview = async () => {
    if (!dialog.submission || !dialog.action) return

    try {
      await reviewSubmission({
        id: dialog.submission.id,
        action: dialog.action,
        adminNotes: dialog.adminNotes || undefined,
      }).unwrap()

      setSuccessMessage(
        `Submission "${dialog.submission.name}" by ${dialog.submission.user?.username ?? 'user'} has been ${dialog.action}d.`
      )
      closeDialog()
      refetch()
    } catch (err: unknown) {
      const error = err as { data?: { message?: string } }
      setErrorMessage(error?.data?.message ?? 'Review action failed. Please try again.')
    }
  }

  const actionLabel: Record<ReviewAction, string> = {
    approve: 'Approve',
    reject: 'Reject',
    request_modification: 'Request Modification',
  }

  const submissions = data?.data ?? []

  return (
    <AdminPageGate permission="content:read" title="Submissions">
    <Box sx={{ p: 3 }}>
      <Typography variant="h4" gutterBottom>
        Cigar Submission Review Queue
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
        Review user-submitted cigars. Approve to add them to the public database, reject to decline,
        or request modifications.
      </Typography>

      {successMessage && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccessMessage(null)}>
          {successMessage}
        </Alert>
      )}
      {errorMessage && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setErrorMessage(null)}>
          {errorMessage}
        </Alert>
      )}

      <Box sx={{ mb: 2, display: 'flex', gap: 2, alignItems: 'center' }}>
        <FormControl size="small" sx={{ minWidth: 160 }}>
          <InputLabel>Status Filter</InputLabel>
          <Select value={statusFilter} label="Status Filter" onChange={handleStatusFilterChange}>
            <MenuItem value="">All</MenuItem>
            <MenuItem value="pending">Pending</MenuItem>
            <MenuItem value="approved">Approved</MenuItem>
            <MenuItem value="rejected">Rejected</MenuItem>
            <MenuItem value="modification_requested">Modification Requested</MenuItem>
          </Select>
        </FormControl>
        <Typography variant="body2" color="text.secondary">
          {data?.pagination?.totalCount ?? 0} submission(s)
        </Typography>
      </Box>

      <Paper>
        {isLoading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 4 }}>
            <CircularProgress />
          </Box>
        ) : submissions.length === 0 ? (
          <Box sx={{ py: 4, textAlign: 'center' }}>
            <Typography color="text.secondary">No submissions found.</Typography>
          </Box>
        ) : (
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Cigar</TableCell>
                  <TableCell>Brand</TableCell>
                  <TableCell>Vitola</TableCell>
                  <TableCell>Wrapper</TableCell>
                  <TableCell>Origin</TableCell>
                  <TableCell>Submitted By</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Date</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {submissions.map((sub) => (
                  <TableRow key={sub.id}>
                    <TableCell>
                      <Typography variant="body2" fontWeight="medium">
                        {sub.name}
                      </Typography>
                      {sub.description && (
                        <Typography variant="caption" color="text.secondary">
                          {sub.description.slice(0, 60)}
                          {sub.description.length > 60 ? '...' : ''}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>{sub.brand}</TableCell>
                    <TableCell>{sub.vitola}</TableCell>
                    <TableCell>{sub.wrapperType}</TableCell>
                    <TableCell>{sub.originCountry}</TableCell>
                    <TableCell>
                      <Typography variant="body2">{sub.user?.username ?? '—'}</Typography>
                      <Typography variant="caption" color="text.secondary">
                        {sub.user?.email ?? ''}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Chip
                        label={sub.status.replace('_', ' ')}
                        color={STATUS_COLORS[sub.status] ?? 'default'}
                        size="small"
                      />
                    </TableCell>
                    <TableCell>{new Date(sub.createdAt).toLocaleDateString()}</TableCell>
                    <TableCell>
                      {(sub.status === 'pending' || sub.status === 'modification_requested') && (
                        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                          <Button
                            size="small"
                            variant="contained"
                            color="success"
                            startIcon={<ApproveIcon />}
                            onClick={() => openReviewDialog(sub, 'approve')}
                          >
                            Approve
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="info"
                            startIcon={<ModifyIcon />}
                            onClick={() => openReviewDialog(sub, 'request_modification')}
                          >
                            Modify
                          </Button>
                          <Button
                            size="small"
                            variant="outlined"
                            color="error"
                            startIcon={<RejectIcon />}
                            onClick={() => openReviewDialog(sub, 'reject')}
                          >
                            Reject
                          </Button>
                        </Box>
                      )}
                      {sub.status === 'approved' && sub.adminNotes && (
                        <Typography variant="caption" color="text.secondary">
                          {sub.adminNotes}
                        </Typography>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        )}
      </Paper>

      {/* Review Dialog */}
      <Dialog open={dialog.open} onClose={closeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialog.action ? actionLabel[dialog.action] : ''} Submission
        </DialogTitle>
        <DialogContent>
          {dialog.submission && (
            <Box sx={{ mb: 2 }}>
              <Typography variant="body2">
                <strong>Cigar:</strong> {dialog.submission.name} — {dialog.submission.brand}
              </Typography>
              <Typography variant="body2">
                <strong>Vitola:</strong> {dialog.submission.vitola} |{' '}
                <strong>Wrapper:</strong> {dialog.submission.wrapperType} |{' '}
                <strong>Origin:</strong> {dialog.submission.originCountry}
              </Typography>
            </Box>
          )}
          <TextField
            label="Admin Notes (optional)"
            value={dialog.adminNotes}
            onChange={(e) => setDialog((prev) => ({ ...prev, adminNotes: e.target.value }))}
            fullWidth
            multiline
            rows={3}
            placeholder={
              dialog.action === 'request_modification'
                ? 'Describe what needs to be changed...'
                : 'Optional notes for the submitter...'
            }
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={closeDialog} disabled={isReviewing}>
            Cancel
          </Button>
          <Button
            onClick={handleReview}
            variant="contained"
            color={
              dialog.action === 'approve'
                ? 'success'
                : dialog.action === 'reject'
                ? 'error'
                : 'primary'
            }
            disabled={isReviewing}
            startIcon={isReviewing ? <CircularProgress size={16} color="inherit" /> : undefined}
          >
            {isReviewing ? 'Processing...' : dialog.action ? actionLabel[dialog.action] : ''}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
    </AdminPageGate>
  )
}

export default AdminSubmissions
